import { _ } from "../../../core/polyglot/polyglot.js";
import { makeMonitorRepository } from "../../../core/repositories/repositoryBuilders.js";
import accountingDocument from "../../shared/accountingDocument.js";
import ActionButton from "../../../core/widgets/ActionButtonWidget/ActionButtonWidget.js";
import EmptyArchiveWidget from "../../../core/widgets/emptyArchive/EmptyArchiveWidget.js";
import ListAndIconViewSwitch from "../../../core/widgets/ListAndIconViewSwitch/ListAndIconViewSwitch.js";
import makeReportNotificationButton from "./makeReportNotificationButton.js";
import NullPeriodReport from "../../../models/accounting/periodReport/NullPeriodReport.js";
import optionsBag from "../../../models/options/optionsBag.js";
import periodReportArchiveIconSectionWidget from "./periodReportArchiveIconSectionWidget.js";
import periodReportArchiveListSectionWidget from "./periodReportArchiveListSectionWidget.js";
import PeriodReportNotificationService from "./PeriodReportNotificationService.js";
import PeriodReportService from "./PeriodReportService.js";
import PeriodReportSummary from "../../../models/accounting/periodReport/PeriodReportSummary.js";
import responsive from "../../../core/responsive.js";
import session from "../../../core/session.js";
import SwitchButton from "../../../core/widgets/SwitchButton/SwitchButton.js";

/**
 * `periodReportArchiveDocument` displays all created period reports.
 */
const periodReportArchiveDocument = accountingDocument.subclass((that, my) => {
	my.initialize = function () {
		my.super(...arguments);

		that.title = _("Period Report Archive");
		that.subtitle = "";
		that.documentId = "period-report-archive";

		my.showAsList = false;
		my.showWithdrawnReports = false;
		my.selectedReport = NullPeriodReport.instance();

		my.periodReportNotificationFeatureToggle = optionsBag.fromId(
			"period-report-email-notification",
		);

		my.companyUsers = [];
		my.agencyUsers = [];

		my.periodReportService = new PeriodReportService();

		that.register("stateChanged", () => {
			my.updateNewReportButtonState();
		});
	};

	my.initializeSubwidgets = function () {
		my.super(...arguments);

		my.newReportButton = new ActionButton({
			label: _("New report"),
			loadingLabel: _("Loading"),
			dataTestName: "new-report",
			click: () => {
				that.showUpdating();
				my.createDraftAndRedirect();
			},
			isPrimary: true,
		});

		my.emptyArchiveWidget = new EmptyArchiveWidget({
			title: _("The period report archive is empty"),
			subtitle: _("There are no published reports in the archive."),
			actions: (html) => my.renderEmptyArchiveActionsOn(html),
		});
	};

	that.help = function () {
		return [
			{
				title: _("Period report archive"),
				content: _(
					"This archive includes reports created by your consultant. The reports can have different period types and will therefore be sorted accordingly. The archive has a document-view and a list-view. The list-view will show you more details about each report.",
				),
			},
			{
				title: _("Create report"),
				content: _("Click this button to create a new report."),
				element: "create-report-button",
				visible: session
					.getCurrentUser()
					.canCreatePeriodReports(session.getCompany()),
			},
			{
				title: _("From latest"),
				content: _(
					"You can use previously published reports as starting points for creating new ones if you want to use the content of the latest publish report.",
				),
				element: "new-from-latest-button",
			},
			{
				title: _("From selected"),
				content: _(
					"If you want to start from another report, you can select that report and then click here.",
				),
				element: "new-from-selected-button",
			},
			{
				title: _("Withdraw"),
				content: _(
					"You can withdraw a report to hide it from the clients view. Select the report to withdraw and an option to withdraw will appear in the right-hand menu. Check this box to display withdrawn reports.",
				),
				element: "show-withdrawn-reports",
				visible: session
					.getCurrentUser()
					.canCreatePeriodReports(session.getCompany()),
			},
			{
				title: _("Draft"),
				content: _(
					"Saved drafts will be found in a separate section. Select a draft by clicking it to continue editing or to delete it.",
				),
				visible: session
					.getCurrentUser()
					.canCreatePeriodReports(session.getCompany()),
			},
		];
	};

	that.hasPeriodPicker = function () {
		return false;
	};

	that.hasActionsMenu = function () {
		return session
			.getCurrentUser()
			.canCreatePeriodReports(session.getCompany());
	};

	that.actions = function () {
		const actions = [];
		const canOpenReport = !my.selectedReport.isDraft();
		const canCreateReports = session
			.getCurrentUser()
			.canCreatePeriodReports(session.getCompany());
		const canEditDraftReport =
			canCreateReports && my.selectedReport.isDraft();
		const canWithdrawReport =
			canCreateReports && my.selectedReport.isPublished();
		const canSendNotification =
			my.selectedReport &&
			!my.selectedReport.isWithdrawn() &&
			canCreateReports;

		if (canCreateReports) {
			actions.push(
				(html) => {
					html.render(
						my.rightMenuSection(
							_("Create report"),
							(html) => {
								html.div(
									{ "data-help": "create-report-button" },
									my.newReportButton,
								);
							},
							(html) => {
								const latestReport =
									my.getLatestPublishedReport();
								if (latestReport) {
									html.a(
										{
											klass: "btn btn-default btn-block",
											"data-help":
												"new-from-latest-button",
											click: () =>
												my.copyReportFromIdAndRedirect(
													latestReport.getId(),
												),
										},
										_("New report from latest"),
									);
								}
							},
							(html) => {
								const selectedReport = my.selectedReport;
								if (selectedReport) {
									html.a(
										{
											klass: "btn btn-default btn-block",
											"data-help":
												"new-from-selected-button",
											click: () =>
												my.copyReportFromIdAndRedirect(
													selectedReport.getId(),
												),
										},
										_("New report from selected"),
									);
								}
							},
						),
					);
				},
				(html) => {
					html.render(
						my.rightMenuSection("", (html) => {
							html.div(
								{
									klass: "right-menu-block",
									"data-help": "show-withdrawn-reports",
								},
								html.span(
									{ klass: "switch-label" },
									_("Withdrawn reports"),
								),
								my.showWithdrawnReportsSwitch,
							);
						}),
					);
				},
			);
		}

		if (!my.selectedReport.isNull()) {
			actions.push((html) => {
				html.render(
					my.rightMenuSection(_("Selected report"), (html) => {
						if (canOpenReport) {
							html.a(
								{
									klass: "btn btn-block btn-primary",
									href: my.linkTo("period-report", {
										id: my.selectedReport.getId(),
									}),
								},
								_("Open report"),
							);
						}
						if (canEditDraftReport) {
							html.a(
								{
									klass: "btn btn-primary btn-block",
									href: my.linkTo("edit-period-report", {
										id: my.selectedReport.getId(),
									}),
								},
								_("Continue edit draft"),
							);

							html.a(
								{ klass: "btn btn-block btn-default" },
								_("Delete draft"),
							).click(() => {
								my.deleteDraftDialog(my.selectedReport);
							});
						}
						if (canWithdrawReport) {
							html.a(
								{ klass: "btn btn-block btn-default" },
								_("Withdraw report"),
							).click(() => {
								my.withdraw(my.selectedReport);
							});
						}
						if (canSendNotification) {
							my.periodReportNotificationFeatureToggle.whenActive(
								() => my.renderEmailNotificationButtonOn(html),
							);
						}
					}),
				);
			});
		}

		return actions;
	};

	const companyId = session.getCompany().getLegacyId();
	const antiForgeryToken = session.getAntiForgeryToken();

	my.periodReportRepository = makeMonitorRepository({
		model: PeriodReportSummary,
		route: "/Accounting2/Company/#companyId/PeriodReport/?action/?id",
		defaultFilters: { companyId },
		antiForgeryToken,
	});

	my.periodReportSummaryRepository = makeMonitorRepository({
		model: PeriodReportSummary,
		route: "/Accounting2/Company/#companyId/PeriodReportSummary/?id",
		defaultFilters: { companyId },
		antiForgeryToken,
	});

	my.notificationService = new PeriodReportNotificationService({ companyId });

	my.reports = [];

	my.switcher = new ListAndIconViewSwitch({
		onListView: function () {
			my.setShowAsList(true);
		},
		onWidgetView: function () {
			my.setShowAsList(false);
		},
		isDefaultViewActive: function () {
			return !my.showAsList;
		},
	});

	my.showWithdrawnReportsSwitch = new SwitchButton({
		state: my.showWithdrawnReports,
		dataTestName: "show-withdrawn",
	});

	my.showWithdrawnReportsSwitch.stateChanged.register((checkState) =>
		my.setShowWithdrawnReports(checkState),
	);

	that.show = function () {
		that.showUpdating();

		my.periodReportSummaryRepository.get().then((reports) => {
			const promises = [];

			promises.push(
				my.notificationService.fetchCompanyUsers(),
				my.notificationService.fetchCompanyAgencyUsers(),
			);

			Promise.all(promises).then((result) => {
				my.companyUsers = result[0];
				my.agencyUsers = result[1];
				my.reports = reports;
				my.showReports();
			});
		});
	};

	my.renderContentOn = function (html) {
		html.render(
			my.section({
				cssClass: "period-report-archive-section",
				dataTestName: "period-report-archive",
				title: [_("Period Reports"), my.renderToggleOn],
				content: function (html) {
					html.div(
						{ klass: "panel" },
						html.div({ klass: "panel-body" }, my.renderReportsOn),
					);
				},
			}),
		);
	};

	my.renderReportsOn = function (html) {
		html.render(
			my.reportSection(_("Months"), my.getMonthReports()),
			my.reportSection(_("Bimesters"), my.getBimesterReports()),
			my.reportSection(_("Quarters"), my.getQuarterReports()),
			my.reportSection(_("Half Years"), my.getHalfYearReports()),
			my.reportSection(_("Years"), my.getYearReports()),
			my.reportSection(_("Customs"), my.getConsecutiveMonthsReports()),
			(html) => {
				if (
					session
						.getCurrentUser()
						.canCreatePeriodReports(session.getCompany())
				) {
					html.render(
						my.reportSection(_("Drafts"), my.getDraftReports()),
					);
				}
				if (
					session
						.getCurrentUser()
						.canCreatePeriodReports(session.getCompany()) &&
					my.showWithdrawnReports
				) {
					html.render(
						my.reportSection(
							_("Withdrawn reports"),
							my.getWithdrawnReports(),
						),
					);
				}
			},
		);
	};

	my.renderEmailNotificationButtonOn = function (html) {
		// Drafts should only be sent to agencyUsers
		const companyUsers = my.selectedReport.isDraft() ? [] : my.companyUsers;
		const company = session.getCompany();

		const service = new PeriodReportNotificationService({
			companyId: company.getLegacyId(),
		});

		html.render(
			makeReportNotificationButton({
				currentUser: session.getCurrentUser(),
				companyUsers,
				agencyUsers: my.agencyUsers,
				report: my.selectedReport,
				company,
				cssClass: "btn-block btn-default",
				sendNotifications: (emailContent) =>
					service.sendNotifications({
						report: my.selectedReport,
						emailContent,
					}),
			}),
		);
	};

	my.renderEmptyArchiveActionsOn = function (html) {
		if (
			!session
				.getCurrentUser()
				.canCreatePeriodReports(session.getCompany())
		) {
			return;
		}

		html.a(
			{
				class: "btn",
				click: () => my.createDraftAndRedirect(),
			},
			_("+ Create your first report"),
		);
	};

	function computeNumberOfVisibleReports() {
		const canCreatePeriodReports = session
			.getCurrentUser()
			.canCreatePeriodReports(session.getCompany());

		return my.reports.filter((report) => {
			if (report.isWithdrawn()) return my.showWithdrawnReports;
			if (report.isDraft()) return canCreatePeriodReports;
			return true;
		}).length;
	}

	my.showReports = function () {
		const numberOfVisibleReports = computeNumberOfVisibleReports();

		if (numberOfVisibleReports) {
			that.showContent();
		} else {
			that.showEmpty();
		}
		my.updateActions();
	};

	my.renderEmptyContentOn = function (html) {
		html.render(my.emptyArchiveWidget);
	};

	my.renderToggleOn = function (html) {
		html.div({ klass: "view-switcher" }, my.switcher);
	};

	my.reportSection = function (title, items) {
		if (!items || !items.length) {
			return function () {};
		}

		const sectionClass = my.showAsList
			? periodReportArchiveListSectionWidget
			: periodReportArchiveIconSectionWidget;
		const section = sectionClass({
			title,
			items,
			canSeePeriodReportViewers: session
				.getCurrentUser()
				.canSeePeriodReportViewers(session.getCompany()),
			selectionPredicate: function (report) {
				return my.selectedReport.getId() === report.getId();
			},
		});

		section.onSelect.register(my.setSelection);
		section.onResetSelection.register(my.resetSelection);

		return section;
	};

	// Actions

	my.withdraw = function (report) {
		my.confirmDeletion({
			title: _("Withdraw {report}?", {
				report: report.getTitle(),
			}),
			body: _("Withdrawing a report cannot be undone."),
			okLabel: _("Withdraw"),
			callback: () => my.withdrawReport(report),
		});
	};

	my.withdrawReport = function (report) {
		my.periodReportRepository
			.update({
				filter: {
					action: "Withdraw",
					id: report.getId(),
				},
				model: report,
			})
			.then(() => {
				that.show();
			});
	};

	my.deleteDraftDialog = function (report) {
		my.confirmDeletion({
			name: report.getTitle(),
			callback: () => my.deleteDraft(report),
		});
	};

	my.deleteDraft = function (report) {
		my.periodReportRepository
			.remove({ filter: { id: report.getId() } })
			.then(() => {
				that.show();
			});
	};

	my.indexOfReport = function (report) {
		for (let i = 0; i < my.reports.length; i++) {
			if (report.getId() === my.reports[i].getId()) {
				return i;
			}
		}
		return -1;
	};

	my.setSelection = function (report) {
		my.selectedReport = report;
		my.updateActions();
		if (
			that.actions().length &&
			!responsive.isMobileOrIPadPortaitBreakpoint()
		) {
			my.showActions();
		}
		that.update();
	};

	my.resetSelection = function () {
		my.setSelection(NullPeriodReport.instance());
		if (
			!that.actions().length &&
			!responsive.isMobileOrIPadPortaitBreakpoint()
		) {
			my.hideActions();
		}
	};

	my.setShowAsList = function (boolean) {
		my.showAsList = boolean;
		that.update();
	};

	my.setShowWithdrawnReports = function (boolean) {
		my.showWithdrawnReports = boolean;
		my.showReports();
	};

	my.getMonthReports = function () {
		return my.reports.filter(
			(report) => report.isMonthReport() && report.isPublished(),
		);
	};

	my.getQuarterReports = function () {
		return my.reports.filter(
			(report) => report.isQuarterReport() && report.isPublished(),
		);
	};

	my.getBimesterReports = function () {
		return my.reports.filter(
			(report) => report.isBimesterReport() && report.isPublished(),
		);
	};

	my.getHalfYearReports = function () {
		return my.reports.filter(
			(report) => report.isHalfYearReport() && report.isPublished(),
		);
	};

	my.getYearReports = function () {
		return my.reports.filter(
			(report) => report.isYearReport() && report.isPublished(),
		);
	};

	my.getConsecutiveMonthsReports = function () {
		return my.reports.filter(
			(report) =>
				report.isConsecutiveMonthsReport() && report.isPublished(),
		);
	};

	my.getWithdrawnReports = function () {
		return my.reports.filter((report) => report.isWithdrawn());
	};

	my.getDraftReports = function () {
		return my.reports.filter((report) => report.isDraft());
	};

	my.getPublishedReports = function () {
		return my.reports.filter((report) => report.isPublished());
	};

	my.getLatestPublishedReport = function () {
		const publishedReports = my.getPublishedReports();
		if (!publishedReports) {
			return null;
		}

		let latestReport = null;
		let latestPublishDate = 0;

		publishedReports.forEach((report) => {
			const publishDate = new Date(report.getPublishDate());
			if (publishDate > latestPublishDate) {
				latestReport = report;
				latestPublishDate = publishDate;
			}
		});

		return latestReport;
	};

	my.updateNewReportButtonState = function () {
		if (!my.newReportButton) {
			return;
		}
		const isLoading = my.state.label === "updating";
		my.newReportButton.setLoading(isLoading);
	};

	my.copyReportFromIdAndRedirect = async function (id) {
		const newDraft = await my.periodReportService.copyReportFromId(id);
		my.redirectToDraftEditor(newDraft);
	};

	my.createDraftAndRedirect = async function () {
		const newDraft = await my.periodReportService.createDraft();
		my.redirectToDraftEditor(newDraft);
	};

	my.redirectToDraftEditor = function (draft) {
		my.redirectTo("edit-period-report", {
			id: draft.getId(),
		});
	};
});

export default periodReportArchiveDocument;
